import { computed, inject } from '@angular/core';
import { Connector, Connectors } from '@interfaces/connector';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { ConnectorsService } from '@services/connectors.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';
import { interval, throwError } from 'rxjs';
import { filter, switchMap, take, timeoutWith } from 'rxjs/operators';

type FilterOption = {
  name: string;
  label: string;
  count: number;
  checked: boolean;
};

type MyAppsState = {
  enabledConnectorsList: Connectors[];
  notEnabledConnectorsList: Connectors[];
  allConnectors: Connectors[];
  connectedFilteredConnectors: Connectors[];
  disconnectedFilteredConnectors: Connectors[];
  loading: boolean;
  processing: boolean;
  pageSize: number;
  currentPage: number;
  paginatorLength: number;
  connectorFilter: string;
  connectorTypeFilter: string;
  connectorFilterMenu: FilterOption[];
  lastUpdated: number | null;
  isRefreshing: boolean;
  isInitialLoad: boolean;
  connectorUpdatedUuid: string | null;
  error: string | null;
  CACHE_DURATION_MS: number;
};

const initialState: MyAppsState = {
  enabledConnectorsList: [],
  notEnabledConnectorsList: [],
  allConnectors: [],
  connectedFilteredConnectors: [],
  disconnectedFilteredConnectors: [],
  loading: false,
  processing: false,
  pageSize: 1000,
  currentPage: 0,
  paginatorLength: 0,
  connectorFilter: '',
  connectorTypeFilter: 'all',
  connectorFilterMenu: [
    { name: 'all', label: 'all', count: 0, checked: true },
    { name: 'active', label: 'active', count: 0, checked: false },
    { name: 'inactive', label: 'inactive', count: 0, checked: false },
    { name: 'pending', label: 'pending_connection', count: 0, checked: false },
  ],
  lastUpdated: null,
  isRefreshing: false,
  isInitialLoad: true,
  connectorUpdatedUuid: null,
  error: null,
  CACHE_DURATION_MS: 5 * 60 * 1000,
};

export const MyAppsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    enabledConnectors: computed(() => store.enabledConnectorsList()),
    notEnabledConnectors: computed(() => store.notEnabledConnectorsList()),
    filteredConnectors: computed(() => {
      const connectors = store.allConnectors();
      if (!store.connectorFilter()) {
        return connectors;
      }
      return connectors.filter((connector) =>
        connector.name.toLowerCase().includes(store.connectorFilter().toLowerCase()),
      );
    }),
    selectedFilter: computed(() => {
      return store.connectorFilterMenu().find((filter) => filter.checked)?.label ?? '';
    }),
    enabledConnectorsLength: computed(() => {
      if (!store.connectorFilter()) {
        return store.enabledConnectorsList().length;
      }
      return store
        .enabledConnectorsList()
        .filter((connector) => connector.name.toLowerCase().includes(store.connectorFilter().toLowerCase())).length;
    }),
    disabledConnectorsLength: computed(() => {
      if (!store.connectorFilter()) {
        return store.notEnabledConnectorsList().length;
      }
      return store
        .notEnabledConnectorsList()
        .filter((connector) => connector.name.toLowerCase().includes(store.connectorFilter().toLowerCase())).length;
    }),
  })),

  withMethods(
    (
      store,
      connectorsService = inject(ConnectorsService),
      userService = inject(UserService),
      spinner = inject(NgxSpinnerService),
      utilsService = inject(UtilsService),
      translate = inject(TranslateService),
    ) => {
      const shouldLoadConnectors = (forceRefresh: boolean): boolean => {
        const now = Date.now();
        const lastUpdated = store.lastUpdated();
        const isStale = !lastUpdated || now - lastUpdated > store.CACHE_DURATION_MS();

        return forceRefresh || store.isInitialLoad() || isStale;
      };

      const resetLoadingState = (success = false) => {
        patchState(store, {
          loading: false,
          isRefreshing: false,
          ...(success && { isInitialLoad: false }),
        });
      };

      /**
       * Loads active connectors from the server.
       * @param forceRefresh - If true, forces a refresh of the connectors.
       */
      const loadActiveConnectors = (forceRefresh = false) => {
        const user = userService.getUserData();
        if (!user?.company?.uuid) {
          patchState(store, {
            error: 'User or company not found',
            loading: false,
          });
          return;
        }

        if (!shouldLoadConnectors(forceRefresh)) {
          return;
        }

        if (store.loading() || store.isRefreshing()) {
          return;
        }

        const paginationParam = `&limit=${store.pageSize()}&offset=${store.currentPage() * store.pageSize()}`;
        connectorsService.getActiveConnectors(user.company.uuid, paginationParam).subscribe({
          next: (res) => {
            patchState(store, {
              paginatorLength: res.count,
              error: null,
              lastUpdated: Date.now(),
            });
            prepareConnectorsList(res.results);
            getFilterDetails();
          },
          error: (error) => {
            patchState(store, {
              error: error.message || 'Failed to load connectors',
            });
          },
          complete: () => {
            resetLoadingState(true);
            spinner.hide();
          },
        });
      };

      /**
       * Prepares the enabled and disabled connectors list.
       * @param connectors - The list of connectors to prepare.
       */
      const prepareConnectorsList = (connectors: Connectors[]) => {
        if (!connectors?.length) {
          patchState(store, {
            enabledConnectorsList: [],
            notEnabledConnectorsList: [],
            allConnectors: [],
            connectedFilteredConnectors: [],
            disconnectedFilteredConnectors: [],
          });
          return;
        }

        const enabled: Connectors[] = [];
        const notEnabled: Connectors[] = [];

        connectors.forEach((connector: Connectors) => {
          if (connector.is_enabled) {
            enabled.push(connector);
          } else {
            notEnabled.push(connector);
          }
        });

        const connectedFiltered = [...enabled, ...notEnabled].filter((connector) => connector.is_enabled);
        const disconnectedFiltered = [...enabled, ...notEnabled].filter((connector) => !connector.is_enabled);

        // Sort disconnected connectors by login status first, then alphabetically
        disconnectedFiltered.sort((a, b) => {
          if (a.is_logged_in && !b.is_logged_in) return -1;
          if (!a.is_logged_in && b.is_logged_in) return 1;
          return a.name.localeCompare(b.name);
        });

        // Sort connected connectors alphabetically
        connectedFiltered.sort((a, b) => a.name.localeCompare(b.name));

        patchState(store, {
          enabledConnectorsList: enabled,
          notEnabledConnectorsList: notEnabled,
          allConnectors: [...enabled, ...notEnabled],
          connectedFilteredConnectors: connectedFiltered,
          disconnectedFilteredConnectors: disconnectedFiltered,
        });

        const selectedFilter = store.connectorFilterMenu().find((filter) => filter.checked)?.name ?? 'all';
        toggleConnectorFilter(selectedFilter);
      };

      /**
       * Get the filter details for the connector filter menu.
       */
      const getFilterDetails = () => {
        const selectedFilter = store.connectorFilterMenu().find((filter) => filter.checked)?.name ?? 'all';
        const allConnectors = store.allConnectors();
        const notEnabledList = store.notEnabledConnectorsList();

        // Fetch translations for filter labels, then build the menu
        getTranslation(translate, ['all', 'active', 'inactive', 'pending_connection']).subscribe((labels) => {
          const filterMenu: FilterOption[] = [
            {
              name: 'all',
              label: labels['all'],
              count: allConnectors.length,
              checked: selectedFilter === 'all',
            },
            {
              name: 'active',
              label: labels['active'],
              count: allConnectors.filter((connector) => connector.is_enabled).length,
              checked: selectedFilter === 'active',
            },
            {
              name: 'inactive',
              label: labels['inactive'],
              count: allConnectors.filter((connector) => !connector.is_enabled).length,
              checked: selectedFilter === 'inactive',
            },
            {
              name: 'pending',
              label: labels['pending_connection'],
              count: notEnabledList.filter((connector) => !connector.is_logged_in).length,
              checked: selectedFilter === 'pending',
            },
          ];

          patchState(store, { connectorFilterMenu: filterMenu });
        });
      };

      /**
       * Toggle the connector filter based on the option selected.
       */
      const toggleConnectorFilter = (option: string) => {
        const allConnectors = store.allConnectors();
        const notEnabledList = store.notEnabledConnectorsList();

        let connectedFiltered: Connectors[] = [];
        let disconnectedFiltered: Connectors[] = [];

        switch (option) {
          case 'all':
            connectedFiltered = allConnectors.filter((connector) => connector.is_enabled);
            disconnectedFiltered = allConnectors.filter((connector) => !connector.is_enabled);
            break;
          case 'active':
            connectedFiltered = allConnectors.filter((connector) => connector.is_enabled);
            disconnectedFiltered = [];
            break;
          case 'inactive':
            connectedFiltered = [];
            disconnectedFiltered = allConnectors.filter((connector) => !connector.is_enabled);
            break;
          case 'pending':
            connectedFiltered = notEnabledList.filter((connector) => !connector.is_logged_in && connector.is_enabled);
            disconnectedFiltered = notEnabledList.filter(
              (connector) => !connector.is_logged_in && !connector.is_enabled,
            );
            break;
        }

        sortConnectorsByType(connectedFiltered, disconnectedFiltered);

        const updatedFilterMenu = store.connectorFilterMenu().map((filter) => ({
          ...filter,
          checked: filter.name === option,
        }));

        patchState(store, {
          connectorTypeFilter: option,
          connectorFilterMenu: updatedFilterMenu,
          connectedFilteredConnectors: connectedFiltered,
          disconnectedFilteredConnectors: disconnectedFiltered,
        });
      };

      /**
       * Sort the connected and disconnected connectors
       */
      const sortConnectorsByType = (connected: Connectors[], disconnected: Connectors[]) => {
        // Sort disconnected by login status then alphabetically
        disconnected.sort((a, b) => {
          if (a.is_logged_in && !b.is_logged_in) return -1;
          if (!a.is_logged_in && b.is_logged_in) return 1;
          return a.name.localeCompare(b.name);
        });

        // Sort connected alphabetically
        connected.sort((a, b) => a.name.localeCompare(b.name));
      };

      /**
       * Enables a connector with the given UUID.
       */
      const enableConnector = async (connectorUuid: string): Promise<void> => {
        const user = userService.getUserData();
        if (!user?.uuid) return;

        patchState(store, { processing: true, connectorUpdatedUuid: connectorUuid });

        try {
          const res = await connectorsService.switchIsEnabledConnector(connectorUuid, true).toPromise();

          const hasDisabledParent = res?.parent?.uuid
            ? store.notEnabledConnectorsList().findIndex((cn) => cn.uuid === res?.parent?.uuid) >= 0
            : false;

          if (hasDisabledParent) {
            const parentRes = await connectorsService.switchIsEnabledConnector(res.parent.uuid, true).toPromise();
            parentRes?.subconnectors?.forEach((child: any) => {
              if (child.uuid !== res.uuid) {
                disableConnector(child.uuid);
              }
            });
            handleToggleConnectorResponse(parentRes);
          } else {
            handleToggleConnectorResponse(res);
            const hasParentEnabled = res?.parent?.uuid
              ? store.enabledConnectorsList().some((cn) => cn.uuid === res?.parent?.uuid)
              : false;

            if (hasParentEnabled) {
              const enabledList = store.enabledConnectorsList();
              const parentIndex = enabledList.findIndex((cn) => cn.uuid === res?.parent?.uuid);
              if (parentIndex >= 0) {
                const indexSubConnector = enabledList[parentIndex].subconnectors?.findIndex(
                  (subCn) => subCn.uuid === res.uuid,
                );
                if (indexSubConnector >= 0) {
                  enabledList[parentIndex].subconnectors[indexSubConnector].is_enabled = true;
                }
              }
            }
          }
        } catch (error) {
          handleConnectorError();
        } finally {
          patchState(store, { processing: false, connectorUpdatedUuid: null });
        }
      };

      /**
       * Disables a connector by its UUID.
       */
      const disableConnector = async (connectorUuid: string): Promise<void> => {
        const user = userService.getUserData();
        if (!user?.uuid) return;

        patchState(store, { processing: true, connectorUpdatedUuid: connectorUuid });

        try {
          const res = await connectorsService.switchIsEnabledConnector(connectorUuid, false).toPromise();

          const hasEnabledParent = res?.parent?.uuid
            ? store.enabledConnectorsList().findIndex((cn) => cn.uuid === res?.parent?.uuid) >= 0
            : false;

          if (hasEnabledParent) {
            const enabledList = store.enabledConnectorsList();
            const indexParentEnabled = enabledList.findIndex((cn) => cn.uuid === res?.parent?.uuid);
            if (
              indexParentEnabled >= 0 &&
              !enabledList[indexParentEnabled].subconnectors?.some((subCn) => subCn.is_enabled)
            ) {
              await disableConnector(res?.parent?.uuid);
            }
          }

          handleToggleConnectorResponse(res);
        } catch (error) {
          handleConnectorError();
        } finally {
          patchState(store, { processing: false, connectorUpdatedUuid: null });
        }
      };

      /**
       * Handles the response from enabling or disabling a connector.
       */
      const handleToggleConnectorResponse = (response: any) => {
        const message = response.is_enabled ? 'connector_enabled' : 'connector_disabled';

        if (response.is_logged_in) {
          getTranslation(translate, message).subscribe((t) => {
            utilsService.showAlert({
              title: t || message,
              alertType: 'success',
              icon: 'circle-check-outline',
              timed: true,
              time: 2000,
              showActionBtn: false,
            });
          });
        } else {
          getTranslation(translate, 'connector_disabled').subscribe((t: any) => {
            utilsService.showAlert({
              title: t,
              alertType: 'warning',
              timed: true,
              time: 2000,
              showActionBtn: false,
            });
          });
        }

        // Update the connector in the list
        const allConnectors = store.allConnectors();
        const connectorIndex = allConnectors.findIndex((cn) => cn.uuid === response.uuid);

        if (connectorIndex >= 0) {
          allConnectors[connectorIndex] = response;
          prepareConnectorsList(allConnectors);
        }

        const selectedFilter = store.connectorFilterMenu().find((filter) => filter.checked)?.name ?? 'all';
        getFilterDetails();
        toggleConnectorFilter(selectedFilter);
      };

      /**
       * Handle connector enable/disable error
       */
      const handleConnectorError = () => {
        patchState(store, { processing: false, connectorUpdatedUuid: null });
        getTranslation(translate, 'error_updating_connector').subscribe((t: any) => {
          utilsService.showAlert({
            title: t,
            alertType: 'error',
            icon: 'circle-x',
            timed: true,
            time: 2000,
            showActionBtn: false,
          });
        });
      };

      /**
       * Toggles all connectors to be enabled or disabled.
       */
      const toggleAllConnectors = async (state: 'disable' | 'enable'): Promise<void> => {
        const user = userService.getUserData();
        if (!user?.company?.uuid) return;

        patchState(store, { loading: true });
        const connectorsState = state === 'enable';
        spinner.show();
        try {
          const res = await connectorsService.toggleAllConnectors(user.company.uuid, connectorsState).toPromise();
          prepareConnectorsList(res.filter((cn: Connector) => cn.status === 'active'));
          getFilterDetails();

          const message = connectorsState ? 'all_connectors_enabled_success' : 'all_connectors_disabled_success';
          getTranslation(translate, message).subscribe((t: any) => {
            utilsService.showAlert({
              title: t || message,
              alertType: 'success',
              icon: 'circle-check-outline',
              timed: true,
              time: 2000,
              showActionBtn: false,
            });
          });
        } catch (error) {
          const message = connectorsState ? 'enable_all_connectors_error' : 'disable_all_connectors_error';
          getTranslation(translate, message).subscribe((t: any) => {
            utilsService.showAlert({
              title: t || message,
              alertType: 'error',
              icon: 'circle-x',
              timed: true,
              time: 2000,
              showActionBtn: false,
            });
          });
        } finally {
          patchState(store, { loading: false });
          spinner.hide();
        }
      };

      /**
       * Get authentication URL for connector login
       */
      const getConnectorLoginUrl = (connector: Connectors): string => {
        const user = userService.getUserData();
        return connectorsService.getAuthenticateConnector(connector.uuid, user?.uuid ?? '', user?.company?.uuid ?? '');
      };

      /**
       * Get logout URL for connector
       */
      const getConnectorLogoutUrl = (connector: Connectors): string => {
        const user = userService.getUserData();
        return connectorsService.getUnAuthenticateConnector(
          connector.uuid,
          user?.uuid ?? '',
          user?.company?.uuid ?? '',
        );
      };

      /**
       * Check connector status after login/logout
       */
      const checkConnectorStatus = (connectorUuid: string, type: 'login' | 'logout' = 'login'): void => {
        patchState(store, { connectorUpdatedUuid: connectorUuid, processing: true });

        interval(5000)
          .pipe(
            switchMap(() => connectorsService.getConnectorByUUID(connectorUuid)),
            filter((data: any) => (type === 'login' ? data.is_logged_in : !data.is_logged_in)),
            take(1),
            timeoutWith(
              60000,
              throwError(() => new Error('Timeout error')),
            ),
          )
          .subscribe({
            next: (response) => {
              handleToggleConnectorResponse(response);
            },
            error: (error) => {
              getTranslation(translate, 'error_updating_connector').subscribe((t: any) => {
                utilsService.showAlert({
                  title: t,
                  alertType: 'error',
                  icon: 'circle-x',
                  timed: true,
                  time: 2000,
                  showActionBtn: false,
                });
              });
            },
            complete: () => {
              patchState(store, { processing: false, connectorUpdatedUuid: null });
            },
          });
      };

      /**
       * Update connector filter text
       */
      const setConnectorFilter = (filter: string) => {
        patchState(store, { connectorFilter: filter });
      };

      /**
       * Handle page change for pagination
       */
      const handlePageChange = (pageSize: number, currentPage: number) => {
        patchState(store, { pageSize, currentPage });
        loadActiveConnectors(true);
      };

      /**
       * Get selected filter count
       */
      const getSelectedFilterCount = (option: string): number => {
        return store.connectorFilterMenu().find((filter) => filter.name === option)?.count ?? 0;
      };

      const clearStore = () => {
        patchState(store, initialState);
      };

      return {
        loadActiveConnectors,
        prepareConnectorsList,
        enableConnector,
        disableConnector,
        toggleAllConnectors,
        getFilterDetails,
        toggleConnectorFilter,
        sortConnectorsByType,
        handleToggleConnectorResponse,
        handleConnectorError,
        getConnectorLoginUrl,
        getConnectorLogoutUrl,
        checkConnectorStatus,
        setConnectorFilter,
        handlePageChange,
        getSelectedFilterCount,
        clearStore,
      };
    },
  ),
);
